import {Button, Grid, TextField} from "@mui/material";
import React, {Dispatch, SetStateAction, useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {useAgGridLocale} from "../../../../../config/globalConfig";
import {ColDef, ICellRendererParams, IHeaderGroupParams, ProcessCellForExportParams} from "ag-grid-community";
import {
    changeCarGroups,
    changeRentalPeriods,
    fromGenericBeToGenericFe,
    heightDynamicMatrix,
    i18nConvertNumberWithDecimals
} from "../../../utils";
import {DEFAULT_MATRIX_COL_DEF, maxNumberYupLimit, SET_SNACKBAR} from "../../../constants";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {useTranslation} from "react-i18next";
import {GridApi, GridOptions} from "ag-grid-enterprise";
import DialogComponent from "../../../common/components/DialogComponent";
import MatrixInitialization from "./MatrixInitialization";
import {
    MatrixDictionary,
    MatrixElementInterface,
    MatrixInitializationValues,
    RateDetail
} from "../../../api/types/rateDetail";
import CheckboxRenderer from "./CheckboxRenderer";
import {useRateDetailRepository} from "../../../api/repositories/RateDetailRepository";
import {useRateRepository} from "../../../api/repositories/RateRepository";
import {useRentalPeriodRepository} from "../../../api/repositories/RentalPeriodRepository";
import {initialValues, validationSchema} from "./FormMatrixInitialization";
import {useNavigate} from "react-router-dom";
import {DialogInterface, SelectElementModelFE} from "../../../api/types/generic";
import {RentalPeriodEntityResponse} from "../../../api/types/rentalPeriods";
import {RateEntityResponse} from "../../../api/types/rate";
import CarGroupsAndRentalPeriods from "../../../common/components/CarGroupsAndRentalPeriods";
import CustomBanner from "../../../common/components/CustomBanner";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {FormProvider, useForm} from "react-hook-form";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import NumberCustomEditorComponent from "../../../common/components/NumberCustomEditorComponent";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import dayjs from "dayjs";
import {useAcrissRepository} from "../../../api/repositories/AcrissRepository";
import {useRecoilState} from "recoil";
import {rentalPeriodsAbsoluteListAtom} from "../../../state/ratesRecoil";
import {mockLogger} from "@tanstack/react-query/build/lib/__tests__/utils";
import { convertToCents } from "../../../../../util/state/commonFunctions";

const RateMatrix = ({
                        rateId,
                        rateInUse,
                        readOnly
                    }: {
    rateId: number | null
    rateInUse: boolean
    readOnly: boolean

}): JSX.Element => {

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use State
    const [matrix, setMatrix] = useState<MatrixDictionary>({});
    const [initialMatrix, setInitialMatrix] = useState<MatrixDictionary>({});
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [absoluteValue, setAbsoluteValue] = useState<boolean>(false)

    // Use Navigate
    const navigate = useNavigate();

    const onGridReady = (params: any) => {
        setGridApi(params.api);
    };

    // Use State
    const [rate, setRate] = useState<RateEntityResponse | null>(null);
    const [rateDetails, setRateDetails] = useState<RateDetail[]>([]);
    const [carGroupRows, setCarGroupRows] = useState<string[]>([]);
    const [allCarGroupRows, setAllCarGroupRows] = useState<string[]>([]);
    const [initialCarGroupRows, setInitialCarGroupRows] = useState<string[]>([]);

    const [rentalPeriodColumns, setRentalPeriodColumns] = useState<RentalPeriodEntityResponse[]>([]);
    const [allRentalPeriodColumns, setAllRentalPeriodColumns] = useState<RentalPeriodEntityResponse[]>([]);
    const [initialRentalPeriodColumns, setInitialRentalPeriodColumns] = useState<RentalPeriodEntityResponse[]>([]);
    const [rateIsFather, setRateIsFather] = useState<boolean>(false)

    const [colMinWidth] = useState<number>(250);
    const [frameworkComponents] = useState<{ [key: string]: any }>({
        checkboxRenderer: CheckboxRenderer,
        customEditor: NumberCustomEditorComponent
    });
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    //Use Recoil
    const [rentalPeriodsAbsoluteList, setRentalPeriodsAbsoluteList] = useRecoilState(rentalPeriodsAbsoluteListAtom)

    // Use Repository Hooks
    const {
        getRateDetails,
        getRateDetailsFromCopyRate,
        getRateDetailsFromExcelFile,
        persistRateDetails,
        deleteRateDetails
    } = useRateDetailRepository();
    const {getRate} = useRateRepository();
    const {getRentalPeriods} = useRentalPeriodRepository();
    const {getOrderedAcrissCarGroupType, orderCarGroupsByAcrissOrder} = useAcrissRepository();
    const {getRentalPeriodsAbsolute, getRentalPeriodsSorted} = useRentalPeriodRepository();
    const {getRateIsFather} = useRateRepository();

    // Use Context
    const {globalData: {user}} = useContext(GlobalContext);

    const daysOfTheWeekUppercase = [
        {code: 1, label: dayjs().day(1).format("dddd").toUpperCase()},
        {code: 2, label: dayjs().day(2).format("dddd").toUpperCase()},
        {code: 3, label: dayjs().day(3).format("dddd").toUpperCase()},
        {code: 4, label: dayjs().day(4).format("dddd").toUpperCase()},
        {code: 5, label: dayjs().day(5).format("dddd").toUpperCase()},
        {code: 6, label: dayjs().day(6).format("dddd").toUpperCase()},
        {code: 7, label: dayjs().day(0).format("dddd").toUpperCase()}
    ];

    // Use Effect
    useEffect(() => {
        const getMatrixInfo = async () => {
            try {
                const rate = await getRate(Number(rateId));
                setRate(rate);

                const rateDetails = (await getRateDetails(Number(rateId)))
                    .map((rateDetail: RateDetail) => {
                        return {
                            ...rateDetail,
                            amount: i18nConvertNumberWithDecimals(Number(rateDetail.amount) / 100),
                            extraKm: rateDetail.unlimitedKm ? "" : i18nConvertNumberWithDecimals(Number(rateDetail.extraKm) / 100),
                        };
                    });
                setRateDetails(rateDetails);

                const rentalPeriodsAbsolute: RentalPeriodEntityResponse[] = await getRentalPeriodsAbsolute();
                setRentalPeriodsAbsoluteList(rentalPeriodsAbsolute);


                if (rateDetails.find(el => el.rentalPeriod.tpUnit === "ABSOLUTE")) {
                    setAbsoluteValue(true)
                }

                let rentalPeriods: RentalPeriodEntityResponse[];
                let carGroups: string[];
                let allRentalPeriods: RentalPeriodEntityResponse[];
                let allCarGroups: string[];

                if (
                    // rate con matrice già esistente
                    rateDetails.length > 0
                ) {
                    const carGroupsAndRentalPeriods = populateCarGroupsAndRentalPeriods({rateDetails});
                    rentalPeriods = carGroupsAndRentalPeriods.rentalPeriods;
                    carGroups = carGroupsAndRentalPeriods.carGroups;
                    allRentalPeriods = (await getRentalPeriods()).filter((rp) => {
                        return rp.tpUnit === rate.tpUnit;
                    }).sort((a, b) => a.from - b.from);
                    allRentalPeriods = await getRentalPeriodsSorted(allRentalPeriods)

                    // togliere CAR_GROUPS || quando si pusha
                    // allCarGroups = CAR_GROUPS.map((c: CarGroup) => c.code) || (await getOrderedAcriss()).map(el => el.acriss);
                    allCarGroups = (await getOrderedAcrissCarGroupType(rate.carGroup, rate.legalEntity)).map((el: { acriss: string; }) => el.acriss);
                    allCarGroups = await orderCarGroupsByAcrissOrder(allCarGroups, rate.legalEntity)
                } else {
                    rentalPeriods = allRentalPeriods = (await getRentalPeriods()).filter((rp) => {
                        return rp.tpUnit === rate.tpUnit;
                    }).sort((a, b) => a.from - b.from);
                    rentalPeriods = await getRentalPeriodsSorted(rentalPeriods)

                    // togliere CAR_GROUPS || quando si pusha
                    // carGroups = allCarGroups = CAR_GROUPS.map((c: CarGroup) => c.code) || (await getOrderedAcriss()).map(el => el.acriss);
                    carGroups = allCarGroups = (await getOrderedAcrissCarGroupType(rate.carGroup, rate.legalEntity)).map((el: { acriss: string; }) => el.acriss);
                    carGroups = await orderCarGroupsByAcrissOrder(allCarGroups, rate.legalEntity)
                }
                setCarGroupsAndRentalPeriodsAndMatrix({
                    rentalPeriods,
                    carGroups,
                    allRentalPeriods,
                    allCarGroups,
                    rateId,
                    rate,
                    rateDetails,
                    setRentalPeriodColumns,
                    setAllRentalPeriodColumns,
                    setInitialRentalPeriodColumns,
                    setCarGroupRows,
                    setAllCarGroupRows,
                    setInitialCarGroupRows,
                    setMatrix,
                    setInitialMatrix,
                    buildMatrix
                });
                setLoading(false);
            } catch (err) {
                setLoading(false);
            }
        };

        getMatrixInfo();
        // eslint-disable-next-line
    }, []);

    const setCarGroupsAndRentalPeriodsAndMatrix = async ({
                                                       rentalPeriods,
                                                       carGroups,
                                                       allRentalPeriods,
                                                       allCarGroups,
                                                       rateId,
                                                       rate,
                                                       rateDetails,
                                                       setRentalPeriodColumns,
                                                       setAllRentalPeriodColumns,
                                                       setInitialRentalPeriodColumns,
                                                       setCarGroupRows,
                                                       setAllCarGroupRows,
                                                       setInitialCarGroupRows,
                                                       setMatrix,
                                                       setInitialMatrix,
                                                       buildMatrix
                                                   }: {
        rentalPeriods: RentalPeriodEntityResponse[]
        carGroups: string[]
        allRentalPeriods: RentalPeriodEntityResponse[]
        allCarGroups: string[]
        rateId: number | null
        rate: RateEntityResponse
        rateDetails: RateDetail[]
        setRentalPeriodColumns: Dispatch<SetStateAction<RentalPeriodEntityResponse[]>>
        setAllRentalPeriodColumns: Dispatch<SetStateAction<RentalPeriodEntityResponse[]>>
        setInitialRentalPeriodColumns: Dispatch<SetStateAction<RentalPeriodEntityResponse[]>>
        setCarGroupRows: Dispatch<SetStateAction<string[]>>
        setAllCarGroupRows: Dispatch<SetStateAction<string[]>>
        setInitialCarGroupRows: Dispatch<SetStateAction<string[]>>
        setMatrix: Dispatch<SetStateAction<MatrixDictionary>>
        setInitialMatrix: Dispatch<SetStateAction<MatrixDictionary>>
        buildMatrix: Function
    }) => {
        setRentalPeriodColumns(rentalPeriods);
        setCarGroupRows(carGroups);
        matrixUpdateForm.reset({
            matrixInitializationType: 0,
            carGroups: carGroups,
            rentalPeriods: fromGenericBeToGenericFe(rentalPeriods),
            rate: null,
            discountIncreasePercentage: null,
            discountIncreaseFixed: null,
            excelFile: null
        });

        setAllRentalPeriodColumns(JSON.parse(JSON.stringify(allRentalPeriods)));
        setAllCarGroupRows(JSON.parse(JSON.stringify(allCarGroups)));
        setInitialRentalPeriodColumns(JSON.parse(JSON.stringify(rentalPeriods)));
        setInitialCarGroupRows(JSON.parse(JSON.stringify(carGroups)));

        //costruire le righe della matrice a partire dal car group e mettendo in fila i periodi di noleggio
        if (rateId && rateDetails.length > 0) {
            let orderedCardGroups = await orderCarGroupsByAcrissOrder(carGroups, rate.legalEntity);
            const accumulator: MatrixDictionary = buildMatrix(rateDetails, rate, rentalPeriods, orderedCardGroups);
            let orderedAccumulator: MatrixDictionary = {};
            orderedCardGroups.forEach((carGroup) => {
                if (accumulator.hasOwnProperty(carGroup)) {
                    orderedAccumulator[carGroup] = accumulator[carGroup];
                }
            });

            setMatrix(orderedAccumulator);
            setInitialMatrix(JSON.parse(JSON.stringify(orderedAccumulator)));
        }
    };

    const populateCarGroupsAndRentalPeriods = ({rateDetails}: { rateDetails: RateDetail[] }): { carGroups: string[], rentalPeriods: RentalPeriodEntityResponse[] } => {
        let rentalPeriods: RentalPeriodEntityResponse[] = [];
        let carGroups: string[] = [];

        rateDetails.forEach(rateDetail => {
            if (!rentalPeriods.find(rp => rp.id === rateDetail.rentalPeriod.id)) {
                rentalPeriods.push(rateDetail.rentalPeriod);
            }

            if (!carGroups.find(cg => cg === rateDetail.carGroup)) {
                carGroups.push(rateDetail.carGroup);
            }
        });
        return {
            carGroups,
            rentalPeriods
        };
    };

    const mapRateDetailsAndCheckUnlimitedKm = ({
                                                   rateDetails,
                                                   unlimitedKm
                                               }: { rateDetails: RateDetail[], unlimitedKm: boolean }): RateDetail[] => {
        return rateDetails.map((rateDetail: RateDetail) => {
            return {
                ...rateDetail,
                amount: Number(rateDetail.amount) / 100,
                extraKm: unlimitedKm ? 0 : Number(rateDetail.extraKm) / 100,
                unlimitedKm: unlimitedKm || rateDetail.unlimitedKm,
            };
        });
    };

    const cellValueSetter = (params: any) => {
        console.log(params)
        if (params?.newValue !== null && params?.newValue !== undefined) {
            params.newValue = (params.newValue + '').replace(/,/g, '.');
        }
        if (!Number(params.newValue) && params.newValue != 0) {
            return false;
        } else if (params.newValue <= maxNumberYupLimit && params.newValue >= 0) {
            params.data[params.colDef.field.split(".")[0]] = {
                ...params.data[params.colDef.field.split(".")[0]],
                amount: params.newValue
            };
            return true;
        } else return false;
    };

    const cellValueSetterExtra = (params: any) => {
        console.log(params)
        if (params?.newValue !== null && params?.newValue !== undefined) {
            params.newValue = (params.newValue + '').replace(/,/g, '.');
        }
        if (!Number(params.newValue) && params.newValue != 0) {
            return false;
        } else if (params.newValue <= maxNumberYupLimit && params.newValue >= 0) {
            params.data[params.colDef.field.split(".")[0]] = {
                ...params.data[params.colDef.field.split(".")[0]],
                extraKm: params.newValue
            };
            return true;
        } else return false;
    };

    const buildMatrixColDef = ({
                                   rentalPeriodColumns,
                                   t,
                                   matrix,
                                   rate,
                                   colMinWidth
                               }: {
        rentalPeriodColumns: RentalPeriodEntityResponse[]
        t: Function
        matrix: MatrixDictionary
        rate: RateEntityResponse | null
        colMinWidth: number
    }): ColDef[] => {
        return [
            {
                headerName: t("pricing:rateMatrix.labels.carGroup"),
                headerTooltip: t("pricing:rateMatrix.labels.carGroup"),
                field: "carGroup",
                width: 120,
                editable: false,
                pinned: "left",
                headerClass: ["bordersRight"],
                cellClass: ["bordersRight"]
            },
            ...rentalPeriodColumns.map((rp: RentalPeriodEntityResponse, index: number) => ({
                    //COSTRUZIONE HEADER COLONNE GESTENDO IL FATTO CHE POSSANO ESSERE ANCHE GIORNI DELLA SETTIMANA
                    headerName: `${rp.name} 
                                (${rp.weekend ? daysOfTheWeekUppercase.find(d => d.code === rp.from)?.label : rp.from ? rp.from : (dayjs(rp.calendarFrom).format('DD/MM'))}
                               - ${rp.weekend ? daysOfTheWeekUppercase.find(d => d.code === rp.to)?.label : rp.to ? rp.to : (dayjs(rp.calendarTo).format('DD/MM'))})`,
                    headerTooltip: `${rp.name} 
                                   (${rp.weekend ? daysOfTheWeekUppercase.find(d => d.code === rp.from)?.label : rp.from ? rp.from : (dayjs(rp.calendarFrom).format('DD/MM'))}
                                  - ${rp.weekend ? daysOfTheWeekUppercase.find(d => d.code === rp.to)?.label : rp.to ? rp.to : (dayjs(rp.calendarFrom).format('DD/MM'))})`,
                    headerClass: ["bordersRight"],
                    minWidth: 600,
                    cellStyle: {textAlign: "center"},
                    children: [{
                        headerName: "",
                        field: rp.id + ".forfait",
                        headerClass: ["bordersRight"],
                        wrapText: true,
                        headerGroupComponent: (params: IHeaderGroupParams) => {
                            const currentDetail = Object.values(matrix)[0][rp.id];
                            return <div style={{
                                width: "100%",
                                padding: 0,
                                display: "flex",
                                justifyContent: "space-around",
                                alignItems: "center"
                            }}>
                                <CheckboxRenderer {...params}
                                                  width={colMinWidth}
                                                  field={rp.id + ".forfait"}
                                                  disabled={readOnly}
                                                  defaultValue={currentDetail?.forfait}
                                                  label={t("pricing:labels.forfait")}
                                                  onChange={(e: any) => {
                                                      const newValue = e.target.checked;
                                                      const newMatrix = {...matrix};
                                                      Object.values(newMatrix).forEach((value: any) => {
                                                          value[rp.id].forfait = newValue;
                                                      });
                                                      setMatrix(newMatrix);
                                                  }}
                                />
                                <CheckboxRenderer {...params}
                                                  width={colMinWidth}
                                                  field={rp.id + ".unlimitedKm"}
                                                  disabled={readOnly || rate?.unlimitedKm}
                                                  defaultValue={Boolean(currentDetail?.unlimitedKm)}
                                                  label={t("pricing:rate.form.labels.otherDefinitionLabels.unlimitedKm")}
                                                  onChange={(e: any) => {
                                                      const newValue = e.target.checked;
                                                      const newMatrix = {...matrix};
                                                      Object.values(newMatrix).forEach((value: any) => {
                                                          value[rp.id].unlimitedKm = newValue;
                                                          value[rp.id].includedKm = null;
                                                      });
                                                      setMatrix(newMatrix);
                                                      const rentalPeriodColumn = rp.id + ".includedKm".split(".")[0];

                                                      params.api.forEachNodeAfterFilterAndSort((node: any) => {
                                                          node.setData({
                                                              ...node.data,
                                                              [rentalPeriodColumn]: {
                                                                  ...node.data[rentalPeriodColumn],
                                                                  includedKm: null,
                                                              }
                                                          });
                                                      });
                                                  }}
                                />
                                <TextField
                                    {...params}
                                    name={rp.id + ".includedKm"}
                                    label={t("pricing:rate.form.labels.otherDefinitionLabels.includedKm")}
                                    type="number"
                                    defaultValue={currentDetail?.includedKm as number}
                                    disabled={readOnly || currentDetail?.unlimitedKm as boolean}
                                    style={{
                                        padding: 0,
                                        margin: 0
                                    }}
                                    // onKeyDown={(e: any) => {
                                    //     if (e.key === "Enter") {
                                    //         const newValue = e.target.value;
                                    //         const newMatrix = {...matrix};
                                    //         Object.values(newMatrix).forEach((value: any) => {
                                    //             value[rp.id].includedKm = newValue;
                                    //         });
                                    //         setMatrix(newMatrix);
                                    //     }
                                    // }}
                                    // onChange={(e: any) => {
                                    //     const newValue = e.target.value;
                                    //     const newMatrix = {...matrix};
                                    //     Object.values(newMatrix).forEach((value: any) => {
                                    //         value[rp.id].includedKm = newValue;
                                    //     });
                                    //     setMatrix(newMatrix);
                                    // }}
                                    onBlur={(e: any) => {
                                        const newValue = e.target.value;
                                        const newMatrix = {...matrix};
                                        Object.values(newMatrix).forEach((value: any) => {
                                            value[rp.id].includedKm = newValue;
                                        });
                                        setMatrix(newMatrix);
                                        const rentalPeriodColumn = rp.id + ".includedKm".split(".")[0];
                                        //const currentCheckName = rp.id + ".includedKm".split(".")[1];

                                        params.api.forEachNodeAfterFilterAndSort((node: any) => {
                                            node.setData({
                                                ...node.data,
                                                [rentalPeriodColumn]: {
                                                    ...node.data[rentalPeriodColumn],
                                                    includedKm: newValue,
                                                }
                                            });
                                        });
                                    }}
                                />
                            </div>;
                        },
                        children: [
                            {
                                headerName: rate?.tpUnit,
                                headerTooltip: rate?.tpUnit,
                                field: rp.id + ".amount",
                                colId: "amount",
                                width: rentalPeriodColumns?.length >= 5 ? colMinWidth : null,
                                flex: rentalPeriodColumns?.length >= 5 ? null : 1,
                                editable: !readOnly,
                                cellEditor: "customEditor",
                                valueSetter: cellValueSetter,
                                cellRenderer: (params: ICellRendererParams) => i18nConvertNumberWithDecimals(params.value),
                                cellStyle: {
                                    justifyContent: "flex-end",
                                    textAlign: "right",
                                    borderRight: "1px dotted #E0E0E0",
                                    padding: 5
                                }
                            },
                            {
                                headerName: t("pricing:rateMatrix.labels.extraKm"),
                                headerTooltip: t("pricing:rateMatrix.labels.extraKm"),
                                field: rp.id + ".extraKm",
                                editable: (params: any) => !params.data[rp.id]?.unlimitedKm && !readOnly,
                                width: rentalPeriodColumns?.length >= 5 ? colMinWidth : null,
                                flex: rentalPeriodColumns?.length >= 5 ? null : 1,
                                cellEditor: "customEditor",
                                valueSetter: cellValueSetterExtra,
                                cellRenderer: (params: ICellRendererParams) => !params.value && params?.data[rp.id]?.unlimitedKm ?
                                    null :
                                    i18nConvertNumberWithDecimals(params.value),
                                headerClass: ["bordersRight"],
                                cellStyle: {
                                    justifyContent: "flex-end",
                                    textAlign: "right",
                                    borderRight: "2px solid #E0E0E0",
                                    padding: 5
                                }
                            }
                        ]
                    }]
                }
            ))
        ];
    };

    const buildMatrixDictionary = (rateDetails: RateDetail[]): MatrixDictionary => {
        return rateDetails.reduce((dictionary: MatrixDictionary, detail) => {
            return {
                ...dictionary,
                [detail.carGroup]: {
                    ...dictionary[detail.carGroup],
                    [detail.rentalPeriod.id]: detail
                }
            };
        }, {});
    };

    const insertRateDetailInMatrix = ({
                                          matrixDictionary,
                                          carGroup,
                                          rentalPeriod,
                                          rateDetail
                                      }: { matrixDictionary: MatrixDictionary, carGroup: string, rentalPeriod: RentalPeriodEntityResponse, rateDetail: RateDetail }): MatrixDictionary => {
        const currentRowObject: MatrixElementInterface = matrixDictionary[carGroup];
        if (currentRowObject) matrixDictionary[carGroup] = {
            ...matrixDictionary[carGroup],
            [rentalPeriod.id]: rateDetail
        };
        else matrixDictionary[carGroup] = {
            [rentalPeriod.id]: rateDetail
        };
        return matrixDictionary;
    };

    const buildEmptyRateDetail = ({
                                      rate,
                                      carGroup,
                                      rentalPeriod
                                  }: { rate: RateEntityResponse, carGroup: string, rentalPeriod: RentalPeriodEntityResponse }): RateDetail => {
        return {
            amount: 0,
            carGroup: carGroup,
            extraKm: 0,
            forfait: false,
            unlimitedKm: rate.unlimitedKm,
            includedKm: rate.includedKm,
            rate: rate,
            rateDetailType: {
                id: 1,
                name: "Standard"
            },
            rentalPeriod: rentalPeriod
        };
    };

    const buildMatrix = (rateDetails: RateDetail[], rate: RateEntityResponse, rentalPeriods: RentalPeriodEntityResponse[], carGroups: string[]): MatrixDictionary => {
        let matrixDictionary: MatrixDictionary = buildMatrixDictionary(rateDetails);

        // per ciascuna riga, per ciascuna colonna, se esiste un dettaglio ce lo inserisco, se non esiste ne creo uno con valori a 0
        carGroups.forEach((carGroup: string) => {
            rentalPeriods.forEach((rentalPeriod: RentalPeriodEntityResponse) => {
                const firstLevel = matrixDictionary[carGroup];
                let currentDetail: RateDetail | undefined;
                if (firstLevel) currentDetail = firstLevel[rentalPeriod.id];

                // se esiste un dettaglio ce lo inserisco
                if (currentDetail) {
                    matrixDictionary = insertRateDetailInMatrix({
                        matrixDictionary,
                        carGroup,
                        rentalPeriod,
                        rateDetail: currentDetail
                    });
                }
                // se non esiste ne creo uno con valori a 0
                else {
                    const matchingDetail = rateDetails.find(detail => detail.rentalPeriod.id === rentalPeriod.id);
                    const currentEmptyDetail: RateDetail = buildEmptyRateDetail({
                        rate,
                        carGroup,
                        rentalPeriod
                    });
                    if(matchingDetail) {
                        currentEmptyDetail.includedKm = matchingDetail.includedKm;
                        currentEmptyDetail.unlimitedKm = matchingDetail.unlimitedKm;
                        currentEmptyDetail.forfait = matchingDetail.forfait;
                    }
                    matrixDictionary = insertRateDetailInMatrix({
                        matrixDictionary,
                        carGroup,
                        rentalPeriod,
                        rateDetail: currentEmptyDetail
                    });
                }
            });
        });

        return matrixDictionary;
    };

    // ************ AG GRID START ************ //
    const columnDefs: ColDef[] = buildMatrixColDef({
        rentalPeriodColumns,
        t,
        matrix,
        rate,
        colMinWidth
    });

    const defaultColDef = {...DEFAULT_MATRIX_COL_DEF};

    const gridOptions: GridOptions = {};
    // ************ AG GRID - END ************ //

    const checkMatrix = (): RateDetail[] | undefined => {
        const rowsFromAgGrid: MatrixElementInterface[] = getRowsFromAgGrid();
        console.log(rowsFromAgGrid)
        let extraKmBiggerThanZero = false;
        let rentalPeriodsConsecutive = false;

        try {
            const rentalPeriodColumsFiltered = rentalPeriodColumns.filter(rp => !rp.weekend)
            let rentalPeriodsAreConsecutive: boolean = true;
            rentalPeriodColumsFiltered.forEach((rp, index) => {
                if (rentalPeriodsAreConsecutive && rp.tpUnit === "ABSOLUTE" && rentalPeriodColumsFiltered.length > 1 && index < rentalPeriodColumsFiltered.length - 1) {
                    let date1 = dayjs(rentalPeriodColumsFiltered[index].calendarTo);
                    let date2 = dayjs(rentalPeriodColumsFiltered[index + 1].calendarFrom);
                    rentalPeriodsAreConsecutive = date2.diff(date1, 'minute') === 1;
                } else if (rentalPeriodsAreConsecutive && index < rentalPeriodColumsFiltered.length - 1 && rp.tpUnit != "ABSOLUTE") {
                    rentalPeriodsAreConsecutive = ((rentalPeriodColumsFiltered[index + 1].from - rp.to) === 1) || ((rentalPeriodColumsFiltered[index + 1].from - rp.to) === 0);
                }
            });
            if (!rentalPeriodsAreConsecutive) {
                rentalPeriodsConsecutive = true;
                throw t("pricing:ancillaryMatrix.errors.rentalPeriodsConsecutive");
            }

            const persistArray: RateDetail[] = [];

            for (const matrixElementInterface of rowsFromAgGrid) {
                Object.values(matrixElementInterface).forEach((rateDetail: RateDetail) => {
                    persistArray.push({
                        ...rateDetail,
                        amount: convertToCents(rateDetail.amount),
                        extraKm: (rateDetail.extraKm == null || rateDetail.extraKm == 0) ? null : convertToCents(rateDetail.extraKm),
                    })
                });
            }
            if (rate?.includedKm && persistArray.some(rateDetail => !rateDetail.unlimitedKm && rateDetail.extraKm === 0)) {
                extraKmBiggerThanZero = true;
                throw t("pricing:rateMatrix.errors.extraKmBiggerThanZero");
            } else {
                return persistArray;
            }
        } catch (e) {
            console.log(e);
            if (rentalPeriodsConsecutive) dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("pricing:ancillaryMatrix.errors.rentalPeriodsConsecutive"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
            else if (extraKmBiggerThanZero) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t("pricing:rateMatrix.errors.extraKmBiggerThanZero"),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            }
        }
    };

    const handlePersistMatrix = async (persistArray: RateDetail[]) => {
        try {
            if (rate && persistArray) {
                await persistRateDetails({
                    rateId: rate.id,
                    rateDetails: persistArray
                });

                setDialog(null);
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);

                navigate("../rate/base-rates");
            }
        } catch (err) {
        }
    };

    const handleDeleteMatrix = async () => {
        try {
            await deleteRateDetails(rateDetails
                .filter((rateDetail: RateDetail) => rateDetail.id)
                .map((rateDetail: RateDetail) => rateDetail.id as number)
            );
            setDialog(null);

            dispatchGlobalData(SET_SNACKBAR.SUCCESS);

            setRateDetails([]);
            setMatrix({});

            setCarGroupRows(allCarGroupRows);
            setRentalPeriodColumns(allRentalPeriodColumns);

            setInitialMatrix({});
        } catch (err) {
        }
    };

    const setGridRefColumDefsAndRowData = ({
                                               rentalPeriods,
                                               carGroups,
                                               matrix
                                           }:
                                               {
                                                   rentalPeriods: RentalPeriodEntityResponse[]
                                                   carGroups: string[]
                                                   matrix: MatrixDictionary
                                               }) => {
        const columnDefs: ColDef[] = buildMatrixColDef({
            rentalPeriodColumns: rentalPeriods,
            t,
            matrix,
            rate,
            colMinWidth
        });

        gridRef?.current?.api?.setColumnDefs(columnDefs);
        gridRef?.current?.api?.setRowData(Object.entries(matrix).map(([key, value]) => {
            return {
                carGroup: key,
                ...value
            };
        }));
        gridRef?.current?.api?.refreshHeader();
        gridRef?.current?.api?.refreshCells();

        setMatrix(matrix);

        setCarGroupRows(carGroups.length === 0 ? initialCarGroupRows : carGroups);
        setRentalPeriodColumns(rentalPeriods.length === 0 ? initialRentalPeriodColumns : rentalPeriods);
    };

    const handleCancel = () => {
        setDialog(null);
        const accumulator = JSON.parse(JSON.stringify({...initialMatrix}));

        setGridRefColumDefsAndRowData({
            rentalPeriods: initialRentalPeriodColumns,
            carGroups: initialCarGroupRows,
            matrix: accumulator
        });
        window.close()
    };

    // ************ FORM INITIALIZE MATRIX- START ************ //
    const submitInitialization = async (values: MatrixInitializationValues) => {
        setAbsoluteValue(values.absolute)
        let rentalPeriodsConsecutive = false;
        try {
            // create empty matrix
            if (values.matrixInitializationType === 0 && values.rentalPeriods && values.carGroups && rate) {
                const rentalPeriodsSubGroup: SelectElementModelFE[] = values.rentalPeriods.filter(el => el.code !== 0);
                const carGroupsSubGroup: string[] = values.carGroups.filter(el => el !== t("pricing:actions.selectAll"));

                const actualRentalPeriods: RentalPeriodEntityResponse[] = values.absolute ?
                    rentalPeriodsAbsoluteList.filter((rp: RentalPeriodEntityResponse) => {
                        return rentalPeriodsSubGroup.find((rpSub: SelectElementModelFE) => rpSub.code === rp.id);
                    }) :
                    allRentalPeriodColumns.filter((rp: RentalPeriodEntityResponse) => {
                        return rentalPeriodsSubGroup.find((rpSub: SelectElementModelFE) => rpSub.code === rp.id);
                    });

                const actualRentalPeriodsFiltered = actualRentalPeriods.filter(rp => !rp.weekend)
                let rentalPeriodsAreConsecutive: boolean = true;
                actualRentalPeriodsFiltered.forEach((rp, index) => {
                    if (rentalPeriodsAreConsecutive && values.absolute && actualRentalPeriodsFiltered.length > 1 && index < actualRentalPeriodsFiltered.length - 1) {
                        let date1 = dayjs(actualRentalPeriodsFiltered[index].calendarTo);
                        let date2 = dayjs(actualRentalPeriodsFiltered[index + 1].calendarFrom);
                        rentalPeriodsAreConsecutive = date2.diff(date1, 'minute') === 1;
                    } else if (rentalPeriodsAreConsecutive && index < actualRentalPeriodsFiltered.length - 1 && !values.absolute) {
                        rentalPeriodsAreConsecutive = ((actualRentalPeriodsFiltered[index + 1].from - rp.to) === 1) || ((actualRentalPeriodsFiltered[index + 1].from - rp.to) === 0);
                    }
                });
                if (!rentalPeriodsAreConsecutive) {
                    rentalPeriodsConsecutive = true;
                    throw t("pricing:ancillaryMatrix.errors.rentalPeriodsConsecutive");
                }

                const actualCarGroups: string[] = await orderCarGroupsByAcrissOrder(allCarGroupRows.filter((cg: string) => {
                    return carGroupsSubGroup.find((cgSub: string) => cgSub === cg);
                }), rate.legalEntity);

                const rentalPeriodSorted: RentalPeriodEntityResponse[] = await getRentalPeriodsSorted(actualRentalPeriods)

                //costruire le righe della matrice a partire dal car group e mettendo in fila i periodi di noleggio
                const accumulator: MatrixDictionary = buildMatrix([], rate, rentalPeriodSorted, actualCarGroups);
                let orderedAccumulator: MatrixDictionary = {};
                actualCarGroups.forEach((carGroup) => {
                    if (accumulator.hasOwnProperty(carGroup)) {
                        orderedAccumulator[carGroup] = accumulator[carGroup];
                    }
                });

                setRentalPeriodColumns(rentalPeriodSorted);
                setCarGroupRows(actualCarGroups);

                matrixUpdateForm.setValue("rentalPeriods", rentalPeriodSorted.map(rp => ({
                    code: rp.id,
                    label: rp.name
                })));
                matrixUpdateForm.setValue("carGroups", actualCarGroups);

                setMatrix(orderedAccumulator);

                setInitialMatrix(JSON.parse(JSON.stringify(accumulator)));
            }
            // copy matrix
            else if (values.matrixInitializationType === 1 && values.rate && rate) {

                const rateDetails = mapRateDetailsAndCheckUnlimitedKm({
                    rateDetails: await getRateDetailsFromCopyRate({
                        originalRateId: values.rate.code as number,
                        destinationRateId: rate.id,
                        fixedDiscount: values.discountIncreaseFixed ? convertToCents(values.discountIncreaseFixed) : null,
                        percentDiscount: values.discountIncreasePercentage ? Number(values.discountIncreasePercentage) : null
                    }),
                    unlimitedKm: Boolean(rate.unlimitedKm)
                });

                const carGroupsAndRentalPeriods = populateCarGroupsAndRentalPeriods({rateDetails});
                const rentalPeriods = carGroupsAndRentalPeriods.rentalPeriods;
                const rentalPeriodSorted: RentalPeriodEntityResponse[] = await getRentalPeriodsSorted(rentalPeriods)
                const carGroups = carGroupsAndRentalPeriods.carGroups;

                setCarGroupsAndRentalPeriodsAndMatrix({
                    rentalPeriods: rentalPeriodSorted,
                    carGroups,
                    allRentalPeriods: allRentalPeriodColumns,
                    allCarGroups: allCarGroupRows,
                    rateId,
                    rate,
                    rateDetails,
                    setRentalPeriodColumns,
                    setAllRentalPeriodColumns,
                    setInitialRentalPeriodColumns,
                    setCarGroupRows,
                    setAllCarGroupRows,
                    setInitialCarGroupRows,
                    setMatrix,
                    setInitialMatrix,
                    buildMatrix
                });
            }
            // import matrix from excel
            else if (values.matrixInitializationType === 2 && values.excelFile && rate) {
                const rateDetails = mapRateDetailsAndCheckUnlimitedKm({
                    rateDetails: await getRateDetailsFromExcelFile({rateId: rate.id, excelFile: values.excelFile}),
                    unlimitedKm: Boolean(rate.unlimitedKm)
                });

                const carGroupsAndRentalPeriods = populateCarGroupsAndRentalPeriods({rateDetails});
                const rentalPeriods = carGroupsAndRentalPeriods.rentalPeriods;
                const carGroups = carGroupsAndRentalPeriods.carGroups;

                setCarGroupsAndRentalPeriodsAndMatrix({
                    rentalPeriods,
                    carGroups,
                    allRentalPeriods: allRentalPeriodColumns,
                    allCarGroups: allCarGroupRows,
                    rateId,
                    rate,
                    rateDetails,
                    setRentalPeriodColumns,
                    setAllRentalPeriodColumns,
                    setInitialRentalPeriodColumns,
                    setCarGroupRows,
                    setAllCarGroupRows,
                    setInitialCarGroupRows,
                    setMatrix,
                    setInitialMatrix,
                    buildMatrix
                });
            }
        } catch (e) {
            if (rentalPeriodsConsecutive) dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("pricing:ancillaryMatrix.errors.rentalPeriodsConsecutive"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        }
    };

    const handleSubmit = (values: MatrixInitializationValues) => {
        submitInitialization(values);
    };

    const matrixInitializationForm = useForm<MatrixInitializationValues>({
        defaultValues: initialValues,
        resolver: yupResolver(
            validationSchema({t}),
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: false
    });
    // ************ FORM INITIALIZE MATRIX - END ************ //

    // ************ FORM UPDATE MATRIX - START ************ //
    const updateValues: MatrixInitializationValues = {
        matrixInitializationType: 0,
        carGroups: carGroupRows,
        rentalPeriods: fromGenericBeToGenericFe(rentalPeriodColumns),
        rate: null,
        discountIncreasePercentage: null,
        discountIncreaseFixed: null,
        excelFile: null,
        absolute: false
    };

    const matrixUpdateForm = useForm<MatrixInitializationValues>({
        defaultValues: updateValues,
        resolver: yupResolver(
            validationSchema({t}),
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: true
    });
    // ************ FORM UPDATE MATRIX - END ************ //

    const getRowsFromAgGrid = () => {
        const rowsFromAgGrid: MatrixElementInterface[] = [];
        if (gridApi) gridApi.forEachNodeAfterFilterAndSort((node: any) => {
            const newNodeData = Object.entries(node.data)
                .filter(([key]: [string, any]) => key !== "carGroup")
                .reduce((acc: any, [key, value]: [string, any]) => {
                    return {
                        ...acc,
                        [key]: {...value}
                    };
                }, {});
            rowsFromAgGrid.push(newNodeData);
        });
        return rowsFromAgGrid;
    };

    const handleChangeAgGridStructure = async ({
                                             rentalPeriods,
                                             carGroups
                                         }: {
        rentalPeriods: RentalPeriodEntityResponse[]
        carGroups: string[]
    }) => {
        const rowsFromAgGrid: MatrixElementInterface[] = getRowsFromAgGrid();

        const persistArray: RateDetail[] = [];

        for (const matrixElementInterface of rowsFromAgGrid) {
            Object.values(matrixElementInterface).forEach((rateDetail: RateDetail) => {
                if (
                    rentalPeriods.find(rp => rp.id === rateDetail.rentalPeriod.id)
                    &&
                    carGroups.find(cg => rateDetail.carGroup === cg)
                ) persistArray.push({...rateDetail});
            });
        }

        if (rate) {
            let orderedCarGroups = await orderCarGroupsByAcrissOrder(carGroups, rate.legalEntity);
            const accumulator: MatrixDictionary = buildMatrix(persistArray, rate, rentalPeriods, orderedCarGroups);

            let orderedAccumulator: MatrixDictionary = {};
            orderedCarGroups.forEach((carGroup) => {
                if (accumulator.hasOwnProperty(carGroup)) {
                    orderedAccumulator[carGroup] = accumulator[carGroup];
                }
            });


            setGridRefColumDefsAndRowData({
                rentalPeriods,
                carGroups: orderedCarGroups,
                matrix: orderedAccumulator
            });
        }
    };

    const handleChangeCarGroups = (newCarGroups: string[]) => {
        if (rate) {
            changeCarGroups({
                t,
                newCarGroups,
                handleChangeAgGridStructure,
                allRentalPeriodColumns,
                rentalPeriodColumns
            });
        }
    };

    const handleChangeRentalPeriods = (newRentalPeriods: SelectElementModelFE[]) => {
        if (rate) {
            changeRentalPeriods({
                newRentalPeriods,
                allRentalPeriodColumns,
                handleChangeAgGridStructure,
                initialCarGroupRows,
                carGroupRows,
                rentalPeriodsAbsoluteList,
                absoluteValue,
                getRentalPeriodsSorted
            });
        }
    };

    function processCellFromClipboard(params: ProcessCellForExportParams) {
        // @ts-ignore
        if (!isNaN(params.value.replace(",", "."))) {
            return params.value;
            // @ts-ignore
        } else {
            return "";
        }
    }

    return (
        <Grid container spacing={2} alignItems="start">
            {!readOnly && rateInUse &&
                <CustomBanner message={t("pricing:rateMatrix.banner")} severity={"warning"}/>}
            {loading ?
                null
                :
                Object.entries(matrix).length > 0 ?
                    // Matrix
                    <Grid item xs={12}>
                        {(
                                !rate?.linkedToReferenceTariff &&
                                !readOnly &&
                                matrixInitializationForm.getValues().matrixInitializationType !== 2
                            ) &&
                            <FormProvider {...matrixUpdateForm}>
                                <Grid container spacing={2} pb={2} item xs={12}>
                                    <CarGroupsAndRentalPeriods carGroups={allCarGroupRows}
                                                               rentalPeriods={allRentalPeriodColumns}
                                                               handleChangeCarGroups={handleChangeCarGroups}
                                                               handleChangeRentalPeriods={handleChangeRentalPeriods}
                                                               rentalPeriodsAbsolute={rentalPeriodsAbsoluteList}
                                                               absolute={absoluteValue}
                                    />
                                </Grid>
                            </FormProvider>
                        }
                        <AgGridCustomTable
                            gridRef={gridRef}
                            defaultColDef={defaultColDef}
                            columnDefs={columnDefs}
                            height={heightDynamicMatrix(carGroupRows as string[], 145)}
                            frameworkComponents={{
                                checkboxRenderer: CheckboxRenderer,
                                customEditor: NumberCustomEditorComponent
                            }}
                            pagination={false}
                            localeText={agGridLocale}
                            tooltipShowDelay={0}
                            stopEditingWhenCellsLoseFocus
                            rowData={Object.entries(matrix).map(([key, value]) => ({
                                carGroup: key,
                                ...value
                            }))}
                            rowSelection={"multiple"}
                            suppressRowClickSelection={true}
                            enableRangeSelection={true}
                            enableCellTextSelection={false}
                            onGridReady={onGridReady}
                            processCellFromClipboard={processCellFromClipboard}
                        >
                        </AgGridCustomTable>
                        {!readOnly &&
                            <Grid container item xs={12} justifyContent="space-between" columnGap={2} my={2}>
                                <Grid>
                                    {rateDetails?.length > 0 &&
                                        <Button color={"accent"}
                                                type={"button"}
                                                onClick={() => setDialog({
                                                    open: true,
                                                    message: t("pricing:rateMatrix.dialogContents.deleteRateMatrix"),
                                                    onClickConfirm: handleDeleteMatrix
                                                })}
                                        >
                                            {t("pricing:actions.delete")}
                                        </Button>
                                    }
                                </Grid>
                                <Grid>
                                    {rateId &&
                                        <Button variant="outlined"
                                                sx={{marginRight: "15px"}}
                                                color={"accent"}
                                                type={"button"}
                                                onClick={() => setDialog({
                                                    open: true,
                                                    message: t("pricing:dialogs.messages.looseChanging"),
                                                    onClickConfirm: handleCancel
                                                })}
                                        >
                                            {t("pricing:actions.cancel")}
                                        </Button>
                                    }
                                    <Button color={"accent"}
                                            type={"button"}
                                            onClick={async () => {
                                                const persistArray = checkMatrix();
                                                if (rate?.id) {
                                                    const rateInFather: boolean = await getRateIsFather(rate?.id as number);
                                                    setRateIsFather(rateInFather)
                                                }
                                                if (persistArray) {
                                                    const message = rateId ?
                                                        (
                                                            rateInUse ?
                                                                t("pricing:rateMatrix.banner")
                                                                :
                                                                rate?.linkedToReferenceTariff ?
                                                                    t("pricing:rateMatrix.dialogContents.overrideRateMatrix")
                                                                    : rateIsFather ? t("pricing:rateMatrix.isFather") :
                                                                        rateIsFather && rateInUse ? t("pricing:rateMatrix.isFatherUsed") :
                                                                            t("pricing:rateMatrix.dialogContents.updateRateMatrix")
                                                        )
                                                        :
                                                        t("pricing:rateMatrix.dialogContents.createRateMatrix");
                                                    setDialog({
                                                        open: true,
                                                        message,
                                                        onClickConfirm: () => handlePersistMatrix(persistArray)
                                                    });
                                                }
                                            }}
                                    >
                                        {t("pricing:actions.confirm")}
                                    </Button>
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                    :
                    readOnly ?
                        <Grid item xs={12} justifyContent={"center"} p={2}>
                            {t("pricing:rateMatrixInsert.noMatrix")}
                        </Grid>
                        :
                        // Matrix INIT
                        <Grid item xs={12}>
                            <FormProvider  {...matrixInitializationForm}>
                                <MatrixInitialization carGroups={allCarGroupRows}
                                                      rentalPeriods={allRentalPeriodColumns}
                                                      rateId={rateId}
                                    //TODO UNITID ENUM NO ID
                                                      tpUnit={rate?.tpUnit || null}
                                                      handleCancel={handleCancel}
                                                      handleSubmit={matrixInitializationForm.handleSubmit(handleSubmit)}
                                />
                            </FormProvider>
                        </Grid>
            }
            {dialog?.open &&
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => setDialog(null)}
                    open={dialog.open}
                    message={dialog.message}
                />
            }
        </Grid>
    );
};

export default RateMatrix;